import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarDays,
  FileText,
  Fish,
  MapPin,
  Pencil,
  RotateCcw,
  Sparkles,
  X,
} from "lucide-react";
import "../styles/components/TripSuggestions.css";
import API_BASE, { withApiCredentials } from "../api";
import { trackProductEvent } from "../analytics/trackEvent";

function withTripApiCredentials(options = {}) {
  const token =
    typeof window !== "undefined" ? window.localStorage.getItem("token") : "";
  if (!token) {
    return withApiCredentials(options);
  }

  const headers = new Headers(options.headers || {});
  headers.set("Authorization", `Bearer ${token}`);
  return withApiCredentials({
    ...options,
    headers,
  });
}

function getCountBucket(count) {
  if (count <= 0) return "0";
  if (count <= 3) return "1-3";
  if (count <= 10) return "4-10";
  return "11+";
}

function normalizeSpeciesLookupText(value = "") {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[()]/g, " ")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getSpeciesLookupKeys(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return [];

  const keys = new Set();
  const addKey = (candidate) => {
    const normalized = normalizeSpeciesLookupText(candidate);
    if (normalized) keys.add(normalized);
  };

  addKey(raw);
  addKey(raw.replace(/_/g, " "));
  addKey(raw.replace(/\s*\(.*?\)\s*/g, " "));

  const parentheticalMatch = raw.match(/^(.+?)\s*\(([^)]+)\)$/);
  if (parentheticalMatch) {
    addKey(parentheticalMatch[1]);
    addKey(parentheticalMatch[2]);
  }

  raw.split(/\s*\/\s*/).forEach(addKey);

  return Array.from(keys);
}

function addTripSpeciesLookupEntry(lookup, rawValue, entry) {
  getSpeciesLookupKeys(rawValue).forEach((key) => {
    if (!lookup.has(key)) lookup.set(key, entry);
  });
}

function buildTripSpeciesCatalog(tripSpeciesData) {
  const entries = [];
  const lookup = new Map();

  if (!tripSpeciesData || typeof tripSpeciesData !== "object" || Array.isArray(tripSpeciesData)) {
    return { entries, lookup };
  }

  Object.entries(tripSpeciesData).forEach(([id, rawEntry]) => {
    if (!rawEntry || typeof rawEntry !== "object" || rawEntry.needsCanonicalReview) {
      return;
    }

    const entry = {
      id,
      legacyName: rawEntry.legacyName || "",
      displayName: rawEntry.displayName || rawEntry.legacyName || rawEntry.commonName || "",
      commonName: rawEntry.commonName || rawEntry.displayName || rawEntry.legacyName || "",
      scientificName: rawEntry.scientificName || "",
      aliases: Array.isArray(rawEntry.aliases) ? rawEntry.aliases : [],
      iconPath: rawEntry.iconPath || "",
    };

    entries.push(entry);
    addTripSpeciesLookupEntry(lookup, id, entry);
    addTripSpeciesLookupEntry(lookup, entry.legacyName, entry);
    addTripSpeciesLookupEntry(lookup, entry.displayName, entry);
    addTripSpeciesLookupEntry(lookup, entry.commonName, entry);
    addTripSpeciesLookupEntry(lookup, entry.scientificName, entry);
    entry.aliases.forEach((alias) => addTripSpeciesLookupEntry(lookup, alias, entry));
  });

  return { entries, lookup };
}

function getTripSpeciesSearchText(entry) {
  return [
    entry.id,
    entry.legacyName,
    entry.displayName,
    entry.commonName,
    entry.scientificName,
    ...(entry.aliases || []),
  ]
    .map(normalizeSpeciesLookupText)
    .filter(Boolean)
    .join(" ");
}

export default function TripSuggestions({ onClose, refreshTrips, userId }) {
  const [data, setData] = useState({});
  const [fishList, setFishList] = useState([]);
  const [tripSpeciesCatalog, setTripSpeciesCatalog] = useState({
    entries: [],
    lookup: new Map(),
  });

  const [criteria, setCriteria] = useState({
    species: "",
    month: "",
    monthNumber: null,
    location: "",
    fishingType: "",
    startDate: "",
    endDate: "",
    displayDate: "",
  });

  const { t, i18n } = useTranslation();

  const [trip, setTrip] = useState({ name: "", notes: "" });

  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [locationResults, setLocationResults] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedSpeciesMeta, setSelectedSpeciesMeta] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const locationTimer = useRef(null);

  /* ----------------------------------------
      LOAD DATA
   ---------------------------------------- */
  useEffect(() => {
    fetch("/JSON/trip-data.json")
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error loading trip data:", err));

    fetch("/JSON/fish-data.json")
      .then((res) => res.json())
      .then((json) => setFishList(json))
      .catch((err) => console.error("Error loading fish data:", err));

    fetch("/JSON/trip-species-data.json")
      .then((res) => res.json())
      .then((json) => setTripSpeciesCatalog(buildTripSpeciesCatalog(json)))
      .catch(() => setTripSpeciesCatalog(buildTripSpeciesCatalog(null)));
  }, []);

  /* ----------------------------------------
      INPUT HANDLERS
   ---------------------------------------- */
  const handleCriteriaChange = (e) => {
    const { name, value } = e.target;
    setCriteria((prev) => ({ ...prev, [name]: value }));

    if (name === "species") {
      setSelectedSpeciesMeta(resolveTripSpeciesEntry(value));

      if (value.length > 1) {
        setSuggestions(getSpeciesSuggestions(value));
        setShowSuggestions(true);
      } else {
        setSelectedSpeciesMeta(null);
        setShowSuggestions(false);
      }
    }
  };

  const selectSuggestion = (suggestion) => {
    const speciesName =
      typeof suggestion === "string" ? suggestion : suggestion?.commonName || "";
    setCriteria((prev) => ({ ...prev, species: speciesName }));
    setSelectedSpeciesMeta(
      typeof suggestion === "string"
        ? resolveTripSpeciesEntry(suggestion)
        : suggestion?.meta || resolveTripSpeciesEntry(speciesName)
    );
    setShowSuggestions(false);
  };

  const handleTripInput = (e) => {
    const { name, value } = e.target;
    setTrip((prev) => ({ ...prev, [name]: value }));
  };

  const resetEngine = () => {
    setCriteria({
      species: "",
      month: "",
      monthNumber: null,
      location: "",
      fishingType: "",
      startDate: "",
      endDate: "",
      displayDate: "",
    });
    setTrip({ name: "", notes: "" });
    setResults([]);
    setShowResults(false);
    setSuggestions([]);
    setShowSuggestions(false);
    setLocationResults([]);
    setSelectedTrip(null);
    setSelectedSpeciesMeta(null);
    setShowCloseConfirm(false);
  };

  /* ----------------------------------------
      HELPERS
   ---------------------------------------- */
  function getMonthNumber(monthName) {
    return new Date(`${monthName} 1, 2000`).getMonth() + 1;
  }

  function formatDateLocalized(date, language) {
    if (!date || isNaN(date)) return "";

    return new Intl.DateTimeFormat(language, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date);
  }

  // Convert a season string like "Oct-Mar" to numeric range
  function parseSeasonRange(season) {
    const [startStr, endStr] = season.split("-");
    const start = getMonthNumber(startStr);
    const end = getMonthNumber(endStr);
    return { start, end };
  }

  // Check if selected month is inside a given season
  function isMonthInSeason(month, season) {
    const { start, end } = parseSeasonRange(season);

    // normal season (e.g., MarÃ¢â‚¬â€œSep)
    if (start <= end) {
      return month >= start && month <= end;
    }

    // wrap-around season (e.g., OctÃ¢â‚¬â€œMar)
    return month >= start || month <= end;
  }

  function getMonthNumbersInRange(startDate, endDate, fallbackMonthNumber) {
    if (!startDate) {
      return Number.isInteger(fallbackMonthNumber) ? [fallbackMonthNumber] : [];
    }

    const start = new Date(`${startDate}T00:00:00`);
    const end = endDate ? new Date(`${endDate}T00:00:00`) : start;

    if (isNaN(start) || isNaN(end) || end < start) {
      return Number.isInteger(fallbackMonthNumber) ? [fallbackMonthNumber] : [];
    }

    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const limit = new Date(end.getFullYear(), end.getMonth(), 1);
    const months = new Set();

    while (cursor <= limit && months.size < 24) {
      months.add(cursor.getMonth() + 1);
      cursor.setMonth(cursor.getMonth() + 1);
    }

    return [...months];
  }

  const simplifyPlaceName = (fullName) => {
    if (!fullName) return "";

    const parts = fullName.split(",").map((p) => p.trim());

    if (parts.length >= 3) {
      return [parts[0], parts[parts.length - 2], parts[parts.length - 1]]
        .filter(Boolean)
        .join(", ");
    }

    return fullName;
  };

  const normalizeLocationName = (str = "") => {
    if (!str) return "";

    let clean = str.toLowerCase();

    // 1. Remove numbers and punctuation
    clean = clean
      .replace(/[()]/g, " ")
      .replace(/\d+/g, "")
      .replace(/[^\w\s,]/g, "")
      .replace(/,+/g, ",")
      .replace(/\s*,\s*/g, ",")
      .replace(/\s+/g, " ")
      .trim();

    // 2. Remove generic admin words
    clean = clean.replace(
      /\b(municipality|unit|region|province|district|county|prefecture|department|state|city|emirate|territory|island|islands|republic|kingdom)\b/g,
      ""
    );

    // 3. UAE / Dubai rules
    clean = clean
      .replace(/\bunited arab emirates\b/g, "uae")
      .replace(/\buae\b/g, "uae")
      .replace(/\bdubai dubai uae\b/g, "dubai uae")
      .replace(/\bdubai uae\b/g, "dubai uae")
      .replace(/\bdubai\b/g, "dubai uae");

    // 4. Cleanup formatting
    clean = clean
      .replace(/,{2,}/g, ",")
      .replace(/^,|,$/g, "")
      .trim();

    // 5. Force "dubai uae" format
    if (clean.includes("dubai uae")) {
      clean = "dubai uae";
    }

    return clean;
  };

  const splitNormalizedLocationParts = (str = "") =>
    normalizeLocationName(str)
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);

  const matchesExplicitLocation = (userLocation, dataLocation) => {
    const userParts = splitNormalizedLocationParts(userLocation);
    const dataParts = splitNormalizedLocationParts(dataLocation);

    if (!userParts.length || !dataParts.length) return false;

    if (userParts.length === 1) {
      const userPart = userParts[0];
      return dataParts.some(
        (part) =>
          part === userPart ||
          part.includes(userPart) ||
          userPart.includes(part)
      );
    }

    const userPrimary = userParts[0];
    const dataPrimary = dataParts[0];
    const userCountry = userParts[userParts.length - 1];
    const dataCountry = dataParts[dataParts.length - 1];

    const primaryMatches =
      dataPrimary === userPrimary ||
      dataPrimary.includes(userPrimary) ||
      userPrimary.includes(dataPrimary);

    const countryMatches =
      dataCountry === userCountry ||
      dataCountry.includes(userCountry) ||
      userCountry.includes(dataCountry);

    return primaryMatches && countryMatches;
  };

  const resolveTripSpeciesEntry = (speciesName) => {
    for (const key of getSpeciesLookupKeys(speciesName)) {
      const entry = tripSpeciesCatalog.lookup.get(key);
      if (entry) return entry;
    }

    return null;
  };

  const getSpeciesSuggestions = (query) => {
    const normalizedQuery = normalizeSpeciesLookupText(query);
    if (!normalizedQuery) return [];

    const suggestionsByValue = new Map();
    const addSuggestion = (suggestion) => {
      const value = suggestion?.commonName || "";
      const key = normalizeSpeciesLookupText(value);
      if (!value || suggestionsByValue.has(key)) return;
      suggestionsByValue.set(key, suggestion);
    };

    tripSpeciesCatalog.entries.forEach((entry) => {
      if (!getTripSpeciesSearchText(entry).includes(normalizedQuery)) return;
      addSuggestion({
        commonName: entry.displayName || entry.legacyName || entry.commonName,
        scientificName: entry.scientificName,
        iconPath: entry.iconPath,
        meta: entry,
      });
    });

    if (Array.isArray(fishList)) {
      fishList.forEach((fish) => {
        if (!fish || typeof fish !== "object") return;
        const commonName = fish.commonName || fish.name || fish.common_name || "";
        const scientificName = fish.scientificName || fish.scientific_name || "";
        const searchText = [commonName, scientificName, fish.id]
          .map(normalizeSpeciesLookupText)
          .filter(Boolean)
          .join(" ");

        if (!searchText.includes(normalizedQuery)) return;
        const meta = resolveTripSpeciesEntry(commonName) || resolveTripSpeciesEntry(scientificName);
        addSuggestion({
          commonName,
          scientificName,
          iconPath: meta?.iconPath || "",
          meta,
        });
      });
    }

    return Array.from(suggestionsByValue.values()).slice(0, 7);
  };

  const getTripDataSpeciesKey = (speciesName, meta) => {
    const tripDataKeys = Object.keys(data || {});
    const candidates = [
      meta?.legacyName,
      meta?.displayName,
      meta?.commonName,
      meta?.scientificName,
      ...(meta?.aliases || []),
      speciesName,
    ].filter(Boolean);

    for (const candidate of candidates) {
      const candidateKeys = getSpeciesLookupKeys(candidate);
      const exactMatch = tripDataKeys.find((key) =>
        getSpeciesLookupKeys(key).some((tripKey) => candidateKeys.includes(tripKey))
      );
      if (exactMatch) return exactMatch;
    }

    const normalizedCandidates = candidates.map(normalizeSpeciesLookupText);
    return tripDataKeys.find((key) => {
      const normalizedKey = normalizeSpeciesLookupText(key);
      return normalizedCandidates.some(
        (candidate) =>
          candidate &&
          (normalizedKey.includes(candidate) || candidate.includes(normalizedKey))
      );
    });
  };

  const buildManualSummary = () => {
    const parts = [];

    if (criteria.species) parts.push(criteria.species);
    if (criteria.location) parts.push(criteria.location);

    if (criteria.startDate) {
      let range = criteria.displayDate || criteria.startDate;
      if (criteria.startTime) range += ` ${criteria.startTime}`;

      if (criteria.endDate) {
        const end = new Date(criteria.endDate);
        range += " -> ";
        range += formatDateLocalized(end, i18n.language);
        if (criteria.endTime) range += ` ${criteria.endTime}`;
      }

      parts.push(range);
    }

    if (criteria.fishingType)
      parts.push(criteria.fishingType === "boat" ? t("boat") : t("shore"));

    return parts.join(" - ");
  };

  /* ----------------------------------------
      SEARCH
   ---------------------------------------- */
  const search = () => {
    let { species, location } = criteria;
    const activeSpeciesMeta = selectedSpeciesMeta || resolveTripSpeciesEntry(species);

    const matches = [];

    const hasSpecies = species.trim() !== "";
    const hasMonth = Number.isInteger(criteria.monthNumber);
    const hasLocation = location.trim() !== "";

    // species + month + location
    if (hasSpecies && hasMonth && hasLocation) {
      const raw = species.toLowerCase().replace(/\s*\(.*?\)\s*/g, "").trim();

      const canonical = fishList.find((f) =>
        f.commonName.toLowerCase().startsWith(raw)
      );

      const speciesKey =
        activeSpeciesMeta?.displayName ||
        activeSpeciesMeta?.legacyName ||
        (canonical ? canonical.commonName : species);

      const key = getTripDataSpeciesKey(species, activeSpeciesMeta);

      if (key) {
        const fishSeasons = data[key];
        const activeMonths = getMonthNumbersInRange(
          criteria.startDate,
          criteria.endDate,
          criteria.monthNumber
        );

        Object.entries(fishSeasons).forEach(([seasonRange, locs]) => {
          const isActiveInRange = activeMonths.some((month) =>
            isMonthInSeason(month, seasonRange)
          );

          if (!isActiveInRange) return;

          locs.forEach((loc) => {
            if (!matchesExplicitLocation(location, loc)) return;

            matches.push(
              t("trip_result_best_in", {
                species: speciesKey,
                location: loc,
                season: seasonRange,
              })
            );
          });
        });
      }
    }

    // species + month
    else if (hasSpecies && hasMonth) {
      const raw = species.toLowerCase().replace(/\s*\(.*?\)\s*/g, "").trim();

      const canonical = fishList.find((f) =>
        f.commonName.toLowerCase().startsWith(raw)
      );

      const speciesKey =
        activeSpeciesMeta?.displayName ||
        activeSpeciesMeta?.legacyName ||
        (canonical ? canonical.commonName : species);

      const key = getTripDataSpeciesKey(species, activeSpeciesMeta);

      if (key) {
        const fishSeasons = data[key];
        const monthNumber = criteria.monthNumber;

        Object.entries(fishSeasons).forEach(([seasonRange, locs]) => {
          if (isMonthInSeason(monthNumber, seasonRange)) {
            locs.forEach((loc) =>
              matches.push(
                t("trip_result_best_in", {
                  species: speciesKey,
                  location: loc,
                  season: seasonRange,
                })
              )
            );
          }
        });
      }
    }

    // species + location (no month)
    else if (hasSpecies && hasLocation && !hasMonth) {
      const key = getTripDataSpeciesKey(species, activeSpeciesMeta);

      if (key) {
        const fishSeasons = data[key];
        const foundMonths = [];
        const normUserLoc = normalizeLocationName(location);

        Object.entries(fishSeasons).forEach(([season, locs]) => {
          locs.forEach((loc) => {
            const normDataLoc = normalizeLocationName(loc);

            if (
              normDataLoc.includes(normUserLoc) ||
              normUserLoc.includes(normDataLoc)
            ) {
              foundMonths.push(season);
            }
          });
        });

        if (foundMonths.length > 0) {
          const unique = [...new Set(foundMonths)];
          matches.push(
            t("trip_result_best_months_in", {
              species: key,
              location: simplifyPlaceName(location),
              months: unique.join(", "),
            })
          );
        }
      }
    }

    // date range + location => species
    else if (hasLocation && hasMonth && !hasSpecies) {
      const activeMonths = getMonthNumbersInRange(
        criteria.startDate,
        criteria.endDate,
        criteria.monthNumber
      );
      const seenMatches = new Set();

      Object.entries(data).forEach(([fish, seasons]) => {
        Object.entries(seasons).forEach(([season, locs]) => {
          const isActiveInRange = activeMonths.some((month) =>
            isMonthInSeason(month, season)
          );

          if (!isActiveInRange) return;

          locs.forEach((loc) => {
            if (matchesExplicitLocation(location, loc)) {
              seenMatches.add(
                t("trip_result_active_in", {
                  species: fish,
                  location: loc,
                  season,
                })
              );
            }
          });
        });
      });

      matches.push(...seenMatches);
    }

    // location only
    else if (hasLocation) {
      Object.entries(data).forEach(([fish, seasons]) => {
        Object.entries(seasons).forEach(([season, locs]) => {
          locs.forEach((loc) => {
            if (
              normalizeLocationName(loc).includes(normalizeLocationName(location))
            ) {
              matches.push(
                t("trip_result_active_in", {
                  species: fish,
                  location: loc,
                  season,
                })
              );
            }
          });
        });
      });
    }

    setResults(matches);
    setShowResults(true);
    setSelectedTrip(null);
    trackProductEvent("trip_suggestion_searched", {
      featureArea: "trips",
      metadata: {
        source: "trip_suggestions",
        has_species: hasSpecies,
        has_month: hasMonth,
        has_location: hasLocation,
        has_date_range: Boolean(criteria.startDate && criteria.endDate),
        result_bucket: getCountBucket(matches.length),
      },
    });
  };

  const searchLocation = async (query) => {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        query
      )}&accept-language=en`
    );
    return await r.json();
  };

  const selectTripResult = (value) => {
    setSelectedTrip(value);
    setShowResults(false);
  };

  const hasUnsavedTripDetails = () => {
    const hasCriteriaDetails = Object.values(criteria).some((value) => {
      if (typeof value === "string") return value.trim() !== "";
      return value !== null && value !== undefined && value !== false;
    });

    return (
      hasCriteriaDetails ||
      Boolean(selectedTrip) ||
      Boolean(trip.name.trim()) ||
      Boolean(trip.notes.trim())
    );
  };

  const requestClose = () => {
    if (hasUnsavedTripDetails()) {
      setShowCloseConfirm(true);
      return;
    }

    onClose?.();
  };

  const keepEditing = () => {
    setShowCloseConfirm(false);
  };

  const confirmClose = () => {
    setShowCloseConfirm(false);
    onClose?.();
  };

  const handleSaveTrip = async () => {
    if (!trip.name || (!criteria.species && !selectedTrip)) {
      alert(t("trip_error_missing_fields"));
      return;
    }

    if (!selectedTrip) {
      alert(t("trip_error_select_suggestion"));
      return;
    }

    if (criteria.startDate && criteria.endDate) {
      const from = new Date(
        `${criteria.startDate}T${criteria.startTime || "00:00"}`
      );
      const to = new Date(`${criteria.endDate}T${criteria.endTime || "00:00"}`);

      if (to < from) {
        alert(t("trip_error_invalid_range"));
        return;
      }
    }

    const payload = {
      userId,
      name: trip.name,
      notes: trip.notes || "",
      species: criteria.species,
      month: criteria.month,
      location: criteria.location,
      fishingType: criteria.fishingType,
      startDate: criteria.startDate,
      startTime: criteria.startTime,
      endDate: criteria.endDate,
      endTime: criteria.endTime,
      displayDate: criteria.displayDate,
      selectedTrip: selectedTrip,
    };

    try {
      if (!userId) {
        throw new Error(t("trip_error_missing_auth"));
      }

      const res = await fetch(
        `${API_BASE}/trips/suggest`,
        withTripApiCredentials({
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        })
      );

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || t("trip_error_save_default"));
      }

      const savedTrip = await res.json();
      console.log("Trip saved to backend:", savedTrip);
      trackProductEvent("trip_saved", {
        featureArea: "trips",
        metadata: {
          source: "trip_suggestions",
          has_species: Boolean(criteria.species),
          has_location: Boolean(criteria.location),
          has_dates: Boolean(criteria.startDate),
          has_end_date: Boolean(criteria.endDate),
        },
      });

      // Backend warming now owns this path, preventing duplicate /conditions calls.
      const shouldWarmWeatherFromClient = false;
      if (shouldWarmWeatherFromClient && savedTrip.id && userId) {
        console.log("Ã°Å¸â€Â¥ Warming weather cache for Trip:", savedTrip.id);
        fetch(
          `${API_BASE}/trips/${savedTrip.id}/conditions?userId=${encodeURIComponent(
            userId
          )}`,
          withTripApiCredentials()
        ).catch((err) =>
          console.warn("Background cache warming failed (non-critical):", err)
        );
      }
      if (refreshTrips) refreshTrips();
      if (onClose) onClose();
    } catch (err) {
      console.error("Save trip error:", err);
      alert(t("trip_error_save_failed") + "\n" + err.message);
    }
  };

  /* ----------------------------------------
      RENDER
   ---------------------------------------- */
  const manualSummary = buildManualSummary();
  const canUseManualSummary = manualSummary.trim() !== "";

  return (
    <div className="trip-suggestions-overlay">
      <div className="trip-suggestions-modal">
        <div className="trip-suggestions-card">
          <div className="trip-suggestions-card-header">
            <div className="trip-suggestions-title-cluster">
              <span className="trip-suggestions-title-icon" aria-hidden="true">
                <Sparkles size={22} strokeWidth={2.35} />
              </span>
              <div className="trip-suggestions-title-copy">
                <h2 className="trip-suggestions-card-title">
                  {t("trip_smart_engine_title")}
                </h2>
                <p className="trip-suggestions-card-helper">
                  {t("trip_smart_engine_helper")}
                </p>
              </div>
            </div>

            <div className="trip-suggestions-header-actions">
              <button
                type="button"
                className="trip-suggestions-close-btn"
                onClick={requestClose}
                aria-label={t("common_close")}
              >
                <X size={18} strokeWidth={2.4} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="trip-suggestions-reset-btn"
                onClick={resetEngine}
              >
                <RotateCcw size={15} strokeWidth={2.35} aria-hidden="true" />
                <span>{t("trip_engine_reset")}</span>
              </button>
            </div>
          </div>

          {/* Species */}
          <div className="trip-suggestions-field-row">
            <span className="trip-suggestions-field-icon" aria-hidden="true">
              <Fish size={19} strokeWidth={2.35} />
            </span>
            <div className="trip-suggestions-field-content">
              <div className="trip-suggestions-form-row">
                <label className="trip-suggestions-label">
                  {t("trip_target_species")}
                </label>

                <div className="trip-suggestions-input-wrapper">
                  <input
                    name="species"
                    value={criteria.species}
                    onChange={handleCriteriaChange}
                    placeholder={t("trip_species_placeholder")}
                    className="trip-suggestions-input"
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    autoComplete="off"
                  />

                  {showSuggestions && suggestions.length > 0 && (
                    <ul className="trip-suggestions-dropdown">
                      {suggestions.map((f, i) => (
                        <li
                          key={i}
                          onMouseDown={() => selectSuggestion(f)}
                          className="trip-suggestions-dropdown-item"
                        >
                          {f.commonName}
                          <div className="trip-suggestions-scientific-name">
                            {f.scientificName}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Date Range */}
          <div className="trip-suggestions-field-row trip-suggestions-field-row--dates">
            <span className="trip-suggestions-field-icon" aria-hidden="true">
              <CalendarDays size={19} strokeWidth={2.35} />
            </span>
            <div className="trip-suggestions-field-content">
              <div className="trip-suggestions-form-row">
                <label className="trip-suggestions-label">
                  {t("trip_date_range")}
                </label>

                <div className="trip-suggestions-date-grid">
                  <label className="trip-suggestions-date-field">
                    <span>{t("trip_date_from")}</span>
                    <input
                      type="date"
                      value={criteria.startDate}
                      onChange={(e) => {
                        const iso = e.target.value;

                        if (!iso) {
                          setCriteria((prev) => ({
                            ...prev,
                            startDate: "",
                            startTime: "",
                            endDate: "",
                            endTime: "",
                            month: "",
                            monthNumber: null,
                            displayDate: "",
                          }));
                          return;
                        }

                        const d = new Date(iso);

                        setCriteria((prev) => ({
                          ...prev,
                          startDate: iso,
                          month: d.toLocaleString(i18n.language, { month: "long" }),
                          monthNumber: d.getMonth() + 1,
                          displayDate: formatDateLocalized(d, i18n.language),
                        }));
                      }}
                      className="trip-suggestions-input"
                    />
                  </label>

                  <label className="trip-suggestions-date-field">
                    <span>{t("trip_date_to")}</span>
                    <input
                      type="date"
                      value={criteria.endDate}
                      min={criteria.startDate || undefined}
                      onChange={(e) =>
                        setCriteria((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                        }))
                      }
                      className="trip-suggestions-input"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="trip-suggestions-field-row">
            <span className="trip-suggestions-field-icon" aria-hidden="true">
              <MapPin size={19} strokeWidth={2.35} />
            </span>
            <div className="trip-suggestions-field-content">
              <div className="trip-suggestions-form-row">
                <label className="trip-suggestions-label">{t("trip_location")}</label>

                <div className="trip-suggestions-input-wrapper">
                  <input
                    name="location"
                    value={criteria.location}
                    placeholder={t("trip_location_placeholder")}
                    className="trip-suggestions-input"
                    autoComplete="off"
                    onChange={(e) => {
                      const value = e.target.value;

                      setCriteria((prev) => ({ ...prev, location: value }));

                      if (locationTimer.current) clearTimeout(locationTimer.current);

                      if (value.length < 3) {
                        setLocationResults([]);
                        return;
                      }

                      locationTimer.current = setTimeout(async () => {
                        try {
                          const res = await searchLocation(value);
                          setLocationResults(res.slice(0, 6));
                        } catch (err) {
                          console.error("Location search failed:", err);
                        }
                      }, 400);
                    }}
                  />

                  {locationResults.length > 0 && (
                    <ul className="trip-suggestions-dropdown">
                      {locationResults.map((r) => {
                        const nice = simplifyPlaceName(r.display_name);

                        return (
                          <li
                            key={r.place_id}
                            className="trip-suggestions-dropdown-item"
                            onMouseDown={() => {
                              setCriteria((prev) => ({
                                ...prev,
                                location: nice,
                              }));
                              setLocationResults([]);
                            }}
                          >
                            {nice}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="trip-suggestions-search-btn-wrapper">
            <button
              type="button"
              onClick={search}
              className="trip-suggestions-search-btn"
            >
              <Sparkles size={18} strokeWidth={2.35} aria-hidden="true" />
              {t("trip_get_smart_suggestions")}
            </button>
          </div>

          {(showResults || selectedTrip) && (
            <div className="trip-suggestions-results">
              {showResults && (
                <div>
                  {canUseManualSummary && (
                    <div
                      onClick={() => selectTripResult(manualSummary)}
                      className={`trip-suggestions-result-item ${
                        selectedTrip === manualSummary ? "selected" : ""
                      }`}
                    >
                      {t("trip_keep_inputs")}
                    </div>
                  )}

                  {results.length === 0 ? (
                    <p className="trip-suggestions-no-results">
                      {t("trip_no_suggestions")}
                    </p>
                  ) : (
                    <>
                      {results.map((r, i) => {
                        const selected = selectedTrip === r;

                        return (
                          <div
                            key={i}
                            onClick={() => selectTripResult(r)}
                            className={`trip-suggestions-result-item ${
                              selected ? "selected" : ""
                            }`}
                          >
                            {r}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}

              {selectedTrip && !showResults && (
                <div className="trip-suggestions-selected-box">
                  {t("trip_selected")}: {selectedTrip}
                </div>
              )}
            </div>
          )}

          <div className="trip-suggestions-details-block">
            <h3 className="trip-suggestions-details-title">
              {t("trip_details")}
            </h3>

            <div className="trip-suggestions-field-row">
              <span className="trip-suggestions-field-icon" aria-hidden="true">
                <Pencil size={18} strokeWidth={2.35} />
              </span>
              <div className="trip-suggestions-field-content">
                <div className="trip-suggestions-form-row">
                  <label className="trip-suggestions-label">{t("trip_name")}</label>
                  <input
                    type="text"
                    name="name"
                    value={trip.name}
                    onChange={handleTripInput}
                    className="trip-suggestions-input"
                    placeholder={t("trip_name_placeholder")}
                  />
                </div>
              </div>
            </div>

            <div className="trip-suggestions-field-row">
              <span className="trip-suggestions-field-icon" aria-hidden="true">
                <FileText size={18} strokeWidth={2.35} />
              </span>
              <div className="trip-suggestions-field-content">
                <div className="trip-suggestions-form-row">
                  <label className="trip-suggestions-label">
                    {t("trip_notes_full")}
                  </label>
                  <textarea
                    name="notes"
                    rows="5"
                    placeholder={t("trip_notes_placeholder")}
                    value={trip.notes}
                    onChange={handleTripInput}
                    className="trip-suggestions-input trip-suggestions-textarea"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="trip-suggestions-actions">
            <button
              type="button"
              className="trip-suggestions-cancel-btn"
              onClick={requestClose}
            >
              {t("cancel")}
            </button>
            <button
              type="button"
              className="trip-suggestions-save-btn"
              onClick={handleSaveTrip}
            >
              {t("save")}
            </button>
          </div>
        </div>
      </div>

      {showCloseConfirm && (
        <div className="trip-suggestions-confirm-backdrop" role="presentation">
          <section
            className="trip-suggestions-confirm"
            role="dialog"
            aria-modal="true"
            aria-label={t("trip_close_confirm_title")}
          >
            <h3 className="trip-suggestions-confirm-title">
              {t("trip_close_confirm_title")}
            </h3>
            <p className="trip-suggestions-confirm-copy">
              {t("trip_close_confirm_message")}
            </p>
            <div className="trip-suggestions-confirm-actions">
              <button
                type="button"
                className="trip-suggestions-confirm-btn trip-suggestions-confirm-btn--secondary"
                onClick={keepEditing}
              >
                {t("trip_close_confirm_keep_editing")}
              </button>
              <button
                type="button"
                className="trip-suggestions-confirm-btn trip-suggestions-confirm-btn--danger"
                onClick={confirmClose}
              >
                {t("trip_close_confirm_close")}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
